/**
 * Base Typography Tokens
 * Font families, scales, weights, and variants
 */

export const baseTypography = {
  // Font families
  fontFamily: {
    primary: '"Roboto", "Helvetica", "Arial", sans-serif',
    code: '"Roboto", "Helvetica", "Arial", sans-serif',
  },

  // Font size scale (in rem)
  fontSize: {
    xs: '0.75rem',   // 12px
    sm: '0.875rem',  // 14px
    base: '1rem',    // 16px
    lg: '1.125rem',  // 18px
    xl: '1.25rem',   // 20px
    '2xl': '1.5rem', // 24px
  },

  // Font weights
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line heights
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },

  // Variants (heading, subtitle, body, etc.)
  variants: {
    h1: {
      fontSize: '2rem',      // 32px (was 1.5rem - increased for Fluent 2)
      fontWeight: 600,       // Semibold (was 500 - Fluent 2 uses stronger headings)
      lineHeight: 1.2,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    h2: {
      fontSize: '1.5rem',    // 24px (was 1.2rem - increased for better hierarchy)
      fontWeight: 600,       // Semibold (was 400 - stronger visual hierarchy)
      lineHeight: 1.3,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    h3: {
      fontSize: '1.125rem',  // 18px
      fontWeight: 500,
      lineHeight: 1.3,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    h4: {
      fontSize: '1.0625rem', // 17px
      fontWeight: 500,
      lineHeight: 1.4,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    h5: {
      fontSize: '1.2rem',    // ~19px
      fontWeight: 400,
      lineHeight: 1.4,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    h6: {
      fontSize: '1.0rem',    // 16px
      fontWeight: 400,
      lineHeight: 1.5,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    subtitle1: {
      fontSize: '0.9rem',    // ~14px
      fontWeight: 600,
      lineHeight: 1.5,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    subtitle2: {
      fontSize: '0.875rem',  // 14px
      fontWeight: 500,
      lineHeight: 1.57,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    body1: {
      fontSize: '1rem',      // 16px
      fontWeight: 400,
      lineHeight: 1.5,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    body2: {
      fontSize: '0.875rem',  // 14px
      fontWeight: 400,
      lineHeight: 1.43,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    button: {
      fontSize: '0.875rem',  // 14px
      fontWeight: 500,
      lineHeight: 1.75,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      textTransform: 'none' as const,
    },
    caption: {
      fontSize: '0.75rem',   // 12px
      fontWeight: 400,
      lineHeight: 1.66,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    overline: {
      fontSize: '0.75rem',   // 12px
      fontWeight: 400,
      lineHeight: 2.66,
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      textTransform: 'uppercase' as const,
    },
  },
} as const;

export type BaseTypography = typeof baseTypography;
